import api from '../config/api';

// Categories
export const getCategories = async () => {
  const response = await api.get('/categories');
  return response.data;
};

export const getCategoryById = async (id) => {
  const response = await api.get(`/categories/${id}`);
  return response.data;
};

export const createCategory = async (categoryData) => {
  const formData = new FormData();
  Object.keys(categoryData).forEach(key => {
    if (categoryData[key] !== null && categoryData[key] !== undefined) {
      if (key === 'image' && categoryData[key] instanceof File) {
        formData.append('image', categoryData[key]);
      } else {
        formData.append(key, categoryData[key]);
      }
    }
  });
  const response = await api.post('/categories', formData);
  return response.data;
};

export const updateCategory = async (id, categoryData) => {
  const formData = new FormData();
  Object.keys(categoryData).forEach(key => {
    if (categoryData[key] !== null && categoryData[key] !== undefined) {
      if (key === 'image' && categoryData[key] instanceof File) {
        formData.append('image', categoryData[key]);
      } else {
        formData.append(key, categoryData[key]);
      }
    }
  });
  const response = await api.put(`/categories/${id}`, formData);
  return response.data;
};

export const deleteCategory = async (id) => {
  const response = await api.delete(`/categories/${id}`);
  return response.data;
};

// Products — admin manages the full catalogue, including inactive/hidden items.
export const getProducts = async (filters = {}) => {
  const response = await api.get('/products', { params: { includeInactive: true, ...filters } });
  return response.data;
};

export const getProductById = async (id) => {
  const response = await api.get(`/products/${id}`);
  return response.data;
};

export const createProduct = async (productData, onProgress) => {
  const formData = new FormData();
  const keyMap = {
    category_id: 'categoryId',
    brand_id: 'brandId',
    stock_quantity: 'stockQuantity',
    catalog_pdf_url: 'catalogPdfUrl',
    default_image_url: 'defaultImageUrl',
    discount_percentage: 'discountPercentage',
    name_ar: 'nameAr',
    description_ar: 'descriptionAr',
    is_active: 'isActive',
  };
  const skipKeys = new Set(['default_image_file', 'catalog_pdf_file', 'additional_images']);

  Object.keys(productData).forEach(key => {
    if (skipKeys.has(key)) return;
    const value = productData[key];
    if (value === null || value === undefined) return;
    const backendKey = keyMap[key] || key;
    formData.append(backendKey, value);
  });

  if (productData.default_image_file instanceof File) {
    formData.append('image', productData.default_image_file);
  }
  
  const response = await api.post('/products', formData, {
    timeout: 0, // No timeout
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        } else {
          // If total is not available, estimate based on loaded bytes
          onProgress(Math.min(95, Math.round((progressEvent.loaded / (100 * 1024 * 1024)) * 100)));
        }
      }
    }
  });
  return response.data;
};

export const updateProduct = async (id, productData, onProgress) => {
  const formData = new FormData();
  const hasFile = productData.default_image_file instanceof File;
  
  const keyMap = {
    category_id: 'categoryId',
    brand_id: 'brandId',
    stock_quantity: 'stockQuantity',
    catalog_pdf_url: 'catalogPdfUrl',
    default_image_url: 'defaultImageUrl',
    discount_percentage: 'discountPercentage',
    name_ar: 'nameAr',
    description_ar: 'descriptionAr',
    is_active: 'isActive',
  };
  const skipKeys = new Set(['default_image_file', 'catalog_pdf_file', 'additional_images']);

  Object.keys(productData).forEach(key => {
    if (skipKeys.has(key)) return;
    const value = productData[key];
    if (value === undefined) return;
    const backendKey = keyMap[key] || key;
    formData.append(backendKey, value === null ? '' : value);
  });
  
  if (productData.default_image_file instanceof File) {
    formData.append('image', productData.default_image_file);
  }
  
  try {
    const response = await api.put(`/products/${id}`, formData, {
      timeout: 0, // No timeout
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          } else if (progressEvent.loaded > 0) {
            // If total is not available, estimate based on loaded bytes
            const estimated = Math.min(95, Math.round((progressEvent.loaded / (100 * 1024 * 1024)) * 100));
            onProgress(estimated);
          }
        }
      }
    });
    return response.data;
  } catch (error) {
    console.error('Update error:', error);
    console.error('Error response:', error.response);
    throw error;
  }
};

// Upload multiple product images with progress tracking
export const deleteProductImage = async (imageId) => {
  const response = await api.delete(`/products/images/${imageId}`);
  return response.data;
};

export const uploadProductImages = async (productId, imageFiles, onProgress) => {
  if (!imageFiles || imageFiles.length === 0) return;
  const formData = new FormData();
  imageFiles.forEach((file) => {
    formData.append('images', file);
  });
  const response = await api.post(`/products/${productId}/images`, formData, {
    timeout: 0, // No timeout
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        } else {
          // Estimate progress if total is not available
          onProgress(Math.min(95, Math.round((progressEvent.loaded / (50 * 1024 * 1024)) * 100)));
        }
      }
    }
  });
  return response.data;
};

// Upload product catalog PDF with progress tracking
export const uploadProductCatalog = async (productId, pdfFile, onProgress) => {
  if (!pdfFile) return;
  const formData = new FormData();
  formData.append('pdf', pdfFile);
  const response = await api.post(`/products/${productId}/catalog`, formData, {
    timeout: 0, // No timeout
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        } else {
          // Estimate progress if total is not available
          onProgress(Math.min(95, Math.round((progressEvent.loaded / (100 * 1024 * 1024)) * 100)));
        }
      }
    }
  });
  return response.data;
};

export const deleteProduct = async (id) => {
  const response = await api.delete(`/products/${id}`);
  return response.data;
};

// Product Options
export const getProductOptions = async (productId) => {
  const response = await api.get(`/products/${productId}/options`);
  return response.data;
};

export const createProductOption = async (productId, optionData) => {
  const response = await api.post(`/products/${productId}/options`, optionData);
  return response.data;
};

export const updateProductOption = async (optionId, optionData) => {
  const response = await api.put(`/products/options/${optionId}`, optionData);
  return response.data;
};

export const deleteProductOption = async (optionId) => {
  const response = await api.delete(`/products/options/${optionId}`);
  return response.data;
};

// Option Values
export const addOptionValue = async (optionId, valueData) => {
  const response = await api.post(`/products/options/${optionId}/values`, valueData);
  return response.data;
};

export const updateOptionValue = async (valueId, valueData) => {
  const response = await api.put(`/products/options/values/${valueId}`, valueData);
  return response.data;
};

export const deleteOptionValue = async (valueId) => {
  const response = await api.delete(`/products/options/values/${valueId}`);
  return response.data;
};

// Product Variants
export const getProductVariants = async (productId) => {
  const response = await api.get(`/products/${productId}/variants`);
  return response.data;
};

export const createProductVariant = async (productId, variantData) => {
  const response = await api.post(`/products/${productId}/variants`, variantData);
  return response.data;
};

export const updateProductVariant = async (variantId, variantData) => {
  const response = await api.put(`/products/variants/${variantId}`, variantData);
  return response.data;
};

export const deleteProductVariant = async (variantId) => {
  const response = await api.delete(`/products/variants/${variantId}`);
  return response.data;
};

// Brands
export const getBrands = async () => {
  const response = await api.get('/brands');
  return response.data;
};

export const getBrandById = async (id) => {
  const response = await api.get(`/brands/${id}`);
  return response.data;
};

export const createBrand = async (brandData) => {
  const formData = new FormData();
  Object.keys(brandData).forEach(key => {
    if (brandData[key] !== null && brandData[key] !== undefined) {
      if (key === 'logo' && brandData[key] instanceof File) {
        formData.append('image', brandData[key]);
      } else {
        formData.append(key, brandData[key]);
      }
    }
  });
  const response = await api.post('/brands', formData);
  return response.data;
};

export const updateBrand = async (id, brandData) => {
  const formData = new FormData();
  Object.keys(brandData).forEach(key => {
    if (brandData[key] !== null && brandData[key] !== undefined) {
      if (key === 'logo' && brandData[key] instanceof File) {
        formData.append('image', brandData[key]);
      } else {
        formData.append(key, brandData[key]);
      }
    }
  });
  const response = await api.put(`/brands/${id}`, formData);
  return response.data;
};

export const deleteBrand = async (id) => {
  const response = await api.delete(`/brands/${id}`);
  return response.data;
};

// News
export const getNews = async () => {
  const response = await api.get('/news');
  return response.data;
};

export const getNewsById = async (id) => {
  const response = await api.get(`/news/${id}`);
  return response.data;
};

export const createNews = async (newsData) => {
  const formData = new FormData();
  
  // Add non-file fields - same pattern as createCategory
  Object.keys(newsData).forEach(key => {
    if (newsData[key] !== null && newsData[key] !== undefined) {
      if (key === 'image' && newsData[key] instanceof File) {
        formData.append('image', newsData[key]);
      } else if (key !== 'image') {
        formData.append(key, newsData[key]);
      }
    }
  });
  
  const response = await api.post('/news', formData);
  return response.data;
};

export const updateNews = async (id, newsData) => {
  const formData = new FormData();
  
  // Add non-file fields - same pattern as updateCategory
  Object.keys(newsData).forEach(key => {
    if (newsData[key] !== null && newsData[key] !== undefined) {
      if (key === 'image' && newsData[key] instanceof File) {
        formData.append('image', newsData[key]);
      } else if (key !== 'image') {
        formData.append(key, newsData[key]);
      }
    }
  });
  
  const response = await api.put(`/news/${id}`, formData);
  return response.data;
};

export const deleteNews = async (id) => {
  const response = await api.delete(`/news/${id}`);
  return response.data;
};

// Page Images
export const getPageImages = async () => {
  const response = await api.get('/page-images');
  return response.data;
};

export const getPageImageById = async (id) => {
  const response = await api.get(`/page-images/${id}`);
  return response.data;
};

export const getPageImageByPageName = async (pageName) => {
  const response = await api.get(`/page-images/page/${pageName}`);
  return response.data;
};

export const createPageImage = async (pageImageData) => {
  const formData = new FormData();
  
  // Add non-file fields - always append page_name as it's required
  // Ensure page_name is always sent (even if empty, to trigger validation)
  const pageName = (pageImageData.page_name || '').toString().trim();
  formData.append('page_name', pageName);
  
  if (pageImageData.alt_text) {
    formData.append('alt_text', pageImageData.alt_text.toString());
  }
  if (pageImageData.is_active !== undefined) {
    formData.append('is_active', pageImageData.is_active ? 'true' : 'false');
  }
  
  // Add file uploads
  if (pageImageData.desktop_image_file instanceof File) {
    formData.append('desktop_image', pageImageData.desktop_image_file);
  } else if (pageImageData.desktop_image_url) {
    formData.append('desktop_image_url', pageImageData.desktop_image_url);
  }
  
  if (pageImageData.mobile_image_file instanceof File) {
    formData.append('mobile_image', pageImageData.mobile_image_file);
  } else if (pageImageData.mobile_image_url) {
    formData.append('mobile_image_url', pageImageData.mobile_image_url);
  }
  
  const response = await api.post('/page-images', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const updatePageImage = async (id, pageImageData) => {
  const formData = new FormData();
  
  // Add non-file fields - always append page_name
  if (pageImageData.page_name !== undefined) formData.append('page_name', pageImageData.page_name);
  if (pageImageData.alt_text !== undefined) formData.append('alt_text', pageImageData.alt_text);
  if (pageImageData.is_active !== undefined) formData.append('is_active', pageImageData.is_active);
  
  if (pageImageData.desktop_image_file instanceof File) {
    formData.append('desktop_image', pageImageData.desktop_image_file);
  } else if (pageImageData.desktop_image_url) {
    formData.append('desktop_image_url', pageImageData.desktop_image_url);
  } else if (pageImageData.remove_desktop_image) {
    formData.append('remove_desktop_image', 'true');
  }
  
  if (pageImageData.mobile_image_file instanceof File) {
    formData.append('mobile_image', pageImageData.mobile_image_file);
  } else if (pageImageData.mobile_image_url) {
    formData.append('mobile_image_url', pageImageData.mobile_image_url);
  } else if (pageImageData.remove_mobile_image) {
    formData.append('remove_mobile_image', 'true');
  }
  
  const response = await api.put(`/page-images/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const deletePageImage = async (id) => {
  const response = await api.delete(`/page-images/${id}`);
  return response.data;
};

// Users
export const getUsers = async () => {
  const response = await api.get('/users');
  return response.data;
};

export const getUserById = async (id) => {
  const response = await api.get(`/users/${id}`);
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/users', userData);
  return response.data;
};

export const updateUser = async (id, userData) => {
  const response = await api.put(`/users/${id}`, userData);
  return response.data;
};

export const updateUserPassword = async (id, password) => {
  const response = await api.put(`/users/${id}/password`, { password });
  return response.data;
};

export const deleteUser = async (id) => {
  const response = await api.delete(`/users/${id}`);
  return response.data;
};

// Orders
// Plain list (compatibility). NOTE: without pagination params the API returns at most
// its default window (500 most recent). Prefer getOrdersPage / getAllOrders below.
export const getOrders = async () => {
  const response = await api.get('/orders');
  return normalizeOrdersResponse(response.data).orders;
};

// Default window the server applies when it does not paginate (models/Order.getAll).
export const ORDERS_SERVER_CAP = 500;

// Normalises both response shapes:
//  - paginated contract: { orders, total, page, limit }
//  - legacy plain array (capped at ORDERS_SERVER_CAP, no total available)
export const normalizeOrdersResponse = (data, requested = {}) => {
  if (Array.isArray(data)) {
    return {
      orders: data,
      total: data.length,
      page: 1,
      limit: data.length,
      paginated: false,
      // A full window means there may be more rows the API did not return.
      capped: data.length >= ORDERS_SERVER_CAP,
    };
  }
  const orders = Array.isArray(data?.orders) ? data.orders : [];
  const total = Number.isFinite(Number(data?.total)) ? Number(data.total) : orders.length;
  const page = Number(data?.page) || requested.page || 1;
  const limit = Number(data?.limit) || requested.limit || orders.length || 1;
  return { orders, total, page, limit, paginated: true, capped: false };
};

// One page of orders: GET /orders?page=&limit=&status=&search=
export const getOrdersPage = async ({ page = 1, limit = 100, status, search } = {}) => {
  const params = { page, limit };
  if (status && status !== 'all') params.status = status;
  if (search) params.search = search;
  const response = await api.get('/orders', { params });
  return normalizeOrdersResponse(response.data, { page, limit });
};

// Every order (for analytics): walks the pages until `total` is reached.
// Falls back to the single plain-array response on the legacy API.
export const getAllOrders = async ({ limit = 200, maxPages = 50 } = {}) => {
  const first = await getOrdersPage({ page: 1, limit });
  if (!first.paginated) return first;
  const orders = [...first.orders];
  let page = 1;
  while (orders.length < first.total && page < maxPages && first.orders.length > 0) {
    page += 1;
    const next = await getOrdersPage({ page, limit });
    if (!next.orders.length) break;
    orders.push(...next.orders);
  }
  return { ...first, orders, page, capped: orders.length < first.total };
};

export const getOrderById = async (id) => {
  const response = await api.get(`/orders/${id}`);
  return response.data;
};

export const getOrderByNumber = async (orderNumber) => {
  const response = await api.get(`/orders/number/${orderNumber}`);
  return response.data;
};

export const updateOrderStatus = async (id, status) => {
  const response = await api.put(`/orders/${id}`, { status });
  return response.data;
};

export const updateOrder = async (id, updates) => {
  const response = await api.put(`/orders/${id}`, updates);
  return response.data;
};

export const deleteOrder = async (id) => {
  const response = await api.delete(`/orders/${id}`);
  return response.data;
};

export const markOrderAsRead = async (id) => {
  const response = await api.put(`/orders/${id}/mark-read`);
  return response.data;
};

export const markAllOrdersAsRead = async () => {
  const response = await api.put('/orders/mark-all-read');
  return response.data;
};

export const getUnreadOrdersCount = async () => {
  const response = await api.get('/orders/unread-count');
  return response.data;
};

// Contact Submissions
export const submitContactForm = async (formData) => {
  const response = await api.post('/contact-submissions', formData);
  return response.data;
};

export const getContactSubmissions = async () => {
  const response = await api.get('/contact-submissions');
  return response.data;
};

export const getContactSubmissionById = async (id) => {
  const response = await api.get(`/contact-submissions/${id}`);
  return response.data;
};

export const markContactSubmissionAsRead = async (id) => {
  const response = await api.put(`/contact-submissions/${id}/mark-read`);
  return response.data;
};

export const markAllContactsAsRead = async () => {
  const response = await api.put('/contact-submissions/mark-all-read');
  return response.data;
};

export const updateContactSubmission = async (id, updates) => {
  const response = await api.put(`/contact-submissions/${id}`, updates);
  return response.data;
};

export const deleteContactSubmission = async (id) => {
  const response = await api.delete(`/contact-submissions/${id}`);
  return response.data;
};

export const getUnreadContactCount = async () => {
  const response = await api.get('/contact-submissions/unread-count');
  return response.data;
};

// Shipping Cities
export const getShippingCities = async () => {
  const response = await api.get('/shipping-cities');
  return response.data;
};

export const getActiveShippingCities = async () => {
  const response = await api.get('/shipping-cities/active');
  return response.data;
};

export const getShippingCityById = async (id) => {
  const response = await api.get(`/shipping-cities/${id}`);
  return response.data;
};

export const createShippingCity = async (cityData) => {
  const response = await api.post('/shipping-cities', cityData);
  return response.data;
};

export const updateShippingCity = async (id, cityData) => {
  const response = await api.put(`/shipping-cities/${id}`, cityData);
  return response.data;
};

export const deleteShippingCity = async (id) => {
  const response = await api.delete(`/shipping-cities/${id}`);
  return response.data;
};

// Promo Codes
export const getPromoCodes = async () => {
  const response = await api.get('/promo-codes');
  return response.data;
};

export const getPromoCodeById = async (id) => {
  const response = await api.get(`/promo-codes/${id}`);
  return response.data;
};

export const validatePromoCode = async (code, orderAmount) => {
  const response = await api.post('/promo-codes/validate', { code, orderAmount });
  return response.data;
};

export const createPromoCode = async (promoData) => {
  const response = await api.post('/promo-codes', promoData);
  return response.data;
};

export const updatePromoCode = async (id, promoData) => {
  const response = await api.put(`/promo-codes/${id}`, promoData);
  return response.data;
};

export const deletePromoCode = async (id) => {
  const response = await api.delete(`/promo-codes/${id}`);
  return response.data;
};

// Shipping Settings
export const getShippingSettings = async () => {
  const response = await api.get('/shipping-settings');
  return response.data;
};

export const getShippingSettingById = async (id) => {
  const response = await api.get(`/shipping-settings/${id}`);
  return response.data;
};

export const checkFreeShipping = async (orderAmount, promoCode) => {
  const response = await api.get('/shipping-settings/check-free', {
    params: { orderAmount, promoCode }
  });
  return response.data;
};

export const createShippingSetting = async (settingData) => {
  const response = await api.post('/shipping-settings', settingData);
  return response.data;
};

export const updateShippingSetting = async (id, settingData) => {
  const response = await api.put(`/shipping-settings/${id}`, settingData);
  return response.data;
};

export const deleteShippingSetting = async (id) => {
  const response = await api.delete(`/shipping-settings/${id}`);
  return response.data;
};

// Dashboard Modules
export const getDashboardModules = async () => {
  const response = await api.get('/dashboard-modules');
  return response.data;
};

export const getActiveDashboardModules = async () => {
  const response = await api.get('/dashboard-modules/active');
  return response.data;
};

export const getDashboardModuleById = async (id) => {
  const response = await api.get(`/dashboard-modules/${id}`);
  return response.data;
};

export const getDashboardModuleByKey = async (key) => {
  const response = await api.get(`/dashboard-modules/key/${key}`);
  return response.data;
};

export const createDashboardModule = async (moduleData, onProgress) => {
  const response = await api.post('/dashboard-modules', moduleData, {
    onUploadProgress: onProgress ? (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress(percentCompleted);
    } : undefined
  });
  return response.data;
};

export const updateDashboardModule = async (id, moduleData, onProgress) => {
  const response = await api.put(`/dashboard-modules/${id}`, moduleData, {
    onUploadProgress: onProgress ? (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress(percentCompleted);
    } : undefined
  });
  return response.data;
};

export const deleteDashboardModule = async (id) => {
  const response = await api.delete(`/dashboard-modules/${id}`);
  return response.data;
};

// Site Settings
export const getSiteSettings = async () => {
  const response = await api.get('/site-settings');
  return response.data;
};

export const getAnnouncementSettings = async () => {
  const response = await api.get('/site-settings/announcement');
  return response.data;
};

// About Page Settings
export const getAboutPageSettings = async () => {
  const response = await api.get('/about-page');
  return response.data;
};

export const updateAboutPageSettings = async (aboutPageData) => {
  const formData = new FormData();
  
  // Append text fields
  if (aboutPageData.title !== undefined) formData.append('title', aboutPageData.title);
  if (aboutPageData.description !== undefined) formData.append('description', aboutPageData.description);
  if (aboutPageData.title_ar !== undefined) formData.append('title_ar', aboutPageData.title_ar);
  if (aboutPageData.description_ar !== undefined) formData.append('description_ar', aboutPageData.description_ar);
  if (aboutPageData.company_profile_description !== undefined) formData.append('company_profile_description', aboutPageData.company_profile_description);
  if (aboutPageData.company_profile_description_ar !== undefined) formData.append('company_profile_description_ar', aboutPageData.company_profile_description_ar);
  
  // Handle image file
  if (aboutPageData.section_image_file instanceof File) {
    formData.append('section_image', aboutPageData.section_image_file);
  } else if (aboutPageData.existing_image_url !== undefined) {
    formData.append('existing_image_url', aboutPageData.existing_image_url);
  }
  
  // Handle PDF file
  if (aboutPageData.company_profile_pdf_file instanceof File) {
    formData.append('company_profile_pdf', aboutPageData.company_profile_pdf_file);
  } else if (aboutPageData.existing_pdf_url !== undefined) {
    formData.append('existing_pdf_url', aboutPageData.existing_pdf_url);
  }
  
  const response = await api.put('/about-page', formData);
  return response.data;
};

// Contact Settings
export const getContactSettings = async () => {
  const response = await api.get('/contact-settings');
  return response.data;
};

export const updateContactSettings = async (contactData) => {
  const response = await api.put('/contact-settings', contactData);
  return response.data;
};

export const updateAnnouncementSettings = async (data) => {
  const response = await api.put('/site-settings/announcement/update', data);
  return response.data;
};

export const updateSiteSetting = async (key, value) => {
  const response = await api.put(`/site-settings/${key}`, { value });
  return response.data;
};

// Popup Settings
export const getPopupSettings = async () => {
  const response = await api.get('/popup');
  return response.data;
};

export const updatePopupSettings = async (data) => {
  const response = await api.put('/popup', data);
  return response.data;
};

// ---------------------------------------------------------------------------
// Upload helpers (mirror backend/utils/upload.js limits so bad files are refused
// before they are sent, with a clear message).
// ---------------------------------------------------------------------------
export const IMAGE_MAX_BYTES = 15 * 1024 * 1024; // 15 MB
export const PDF_MAX_BYTES = 50 * 1024 * 1024;   // 50 MB
const IMAGE_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const IMAGE_EXT = /\.(jpe?g|png|gif|webp)$/i;

const formatMB = (bytes) => `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;

// Returns null when the file is acceptable, otherwise a user-facing message.
export const validateImageFile = (file, t = {}) => {
  if (!file) return null;
  const typeOk = IMAGE_MIME.includes(String(file.type || '').toLowerCase()) && IMAGE_EXT.test(file.name || '');
  if (!typeOk) {
    return t.uploadInvalidImageType || `"${file.name}" is not a supported image. Allowed: JPG, PNG, GIF, WEBP.`;
  }
  if (file.size > IMAGE_MAX_BYTES) {
    return t.uploadImageTooLarge || `"${file.name}" is ${formatMB(file.size)}; the maximum image size is ${formatMB(IMAGE_MAX_BYTES)}.`;
  }
  return null;
};

export const validatePdfFile = (file, t = {}) => {
  if (!file) return null;
  const typeOk = String(file.type || '').toLowerCase() === 'application/pdf' && /\.pdf$/i.test(file.name || '');
  if (!typeOk) {
    return t.uploadInvalidPdfType || `"${file.name}" is not a PDF file.`;
  }
  if (file.size > PDF_MAX_BYTES) {
    return t.uploadPdfTooLarge || `"${file.name}" is ${formatMB(file.size)}; the maximum PDF size is ${formatMB(PDF_MAX_BYTES)}.`;
  }
  return null;
};

// Message for a failed save/upload: prefers the server's own message (400/413/409
// bodies carry `error`), then maps well-known statuses, then the fallback.
export const apiErrorMessage = (error, fallback = 'Request failed') => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const serverMsg = data && typeof data === 'object' ? (data.error || data.message) : (typeof data === 'string' && data.length < 300 ? data : '');
  if (status === 413) return serverMsg || `File too large. Images up to ${formatMB(IMAGE_MAX_BYTES)} and PDFs up to ${formatMB(PDF_MAX_BYTES)} are accepted.`;
  if (status === 429) return serverMsg || 'Too many requests. Please wait a moment and try again.';
  if (status === 403) return serverMsg || 'You do not have permission to perform this action.';
  if (status && status >= 400 && status < 500 && serverMsg) return serverMsg;
  if (status >= 500) return serverMsg && !/^internal server error$/i.test(serverMsg) ? serverMsg : fallback;
  if (error?.request && !error?.response) return 'Cannot reach the server. Check your connection and try again.';
  return serverMsg || error?.message || fallback;
};
